import { FieldModel } from '@cibo/core'
import { Theme } from '@cibo/ui'
import ImageNotSupported from '@mui/icons-material/ImageNotSupported'
import { Box, Skeleton } from '@mui/material'
import { memo, useState } from 'react'
import { useBatchedField } from '../../queries'
import { useThumbnailData } from './getThumbnailData'
import { LandThumbnailProfile, LandThumbnailProps } from './types'

const pixelRatio = window.devicePixelRatio

// consumer can send a field or a resourceId
type ThumbnailGeometry =
  | {
      field: LandThumbnailProfile & Partial<FieldModel>
      resourceId?: string
    }
  | {
      field?: LandThumbnailProfile & Partial<FieldModel>
      resourceId: string
    }

const LandThumbnailBase = ({
  field,
  resourceId,
  width,
  height,
  skin,
  style = undefined,
  loadingMode = 'lazy',
}: LandThumbnailProps & ThumbnailGeometry) => {
  const [unresolvedImage, setUnresolvedImage] = useState<boolean>(false)

  // this will be disabled if the field comes with geometry
  const localField = useBatchedField(!field?.geometry ? resourceId : undefined)

  const geometry = field?.geometry || localField.data?.geometry

  const thumbnailData = useThumbnailData({
    geometry,
    width,
    height,
    skin,
    pixelRatio,
  })

  if ([width, height].includes(Number.NaN)) return null

  const imageStyle = {
    height,
    width,
    minHeight: height,
    maxWidth: width,
    ...style,
    backgroundColor: Theme.color.surface.contrast.primary,
  }

  const isInactive = field && field.isInactive?.()

  if (thumbnailData.isLoading || localField.isLoading) {
    return <Skeleton variant="rectangular" {...{ width, height }} style={{ borderRadius: '4px' }} />
  }

  if (thumbnailData.isError || unresolvedImage) {
    return (
      <Box
        sx={{
          ...(imageStyle as any),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ImageNotSupported
          sx={{ color: 'background.paper' }}
          height={height * 0.85}
          width={width * 0.85}
        />
      </Box>
    )
  }

  return (
    <>
      <img
        alt={field?.name || localField.data?.name}
        style={{
          borderRadius: '4px',
          overflow: 'hidden',
          filter: isInactive ? 'grayscale(95%)' : undefined,
          ...imageStyle,
        }}
        src={thumbnailData.data}
        loading={loadingMode}
        onError={() => setUnresolvedImage(true)}
      />
      {isInactive && (
        <Box
          style={{
            content: '',
            position: 'absolute',
            height: height,
            width: width,
            pointerEvents: 'none',
            background:
              'linear-gradient(to left top, transparent 49%, white 49.5%, white 51.5%, transparent 52%)',
          }}
        />
      )}
    </>
  )
}

export const LandThumbnail = memo(LandThumbnailBase)
